<template>
    <div class="flex flex-col gap-2 py-4">
        <div class="inline-flex items-center gap-2">
            <span
                class="border rounded-full w-10 h-10 shrink-0 flex items-center justify-center font-medium uppercase"
                :class="{
                    'bg-secondary-100 text-secondary-600 border-secondary-200': props.variant === 'secondary',
                    'bg-tertiary-50 text-tertiary-400 border-tertiary-100': props.variant === 'tertiary'

                }">
                {{ initials }}
            </span>
            <span class="font-medium capitalize">
                {{ authorName }}
            </span>
        </div>

        <div v-if="bookingLine" class="text-sm text-neutral-400 font-light capitalize">
            {{ bookingLine }}
        </div>

        <p class="font-light break-words">
            {{ remark.remark }}
        </p>

        <!-- Images -->
        <div class="flex flex-row gap-2">
            <Image v-for="image in remark.images" :src="image.image_url" :alt="`Image ${image.image_url}`" preview width="100" height="100" />
        </div>

        <span class="text-neutral-400 font-light">
            {{ createdAt }}
        </span>
    </div>
</template>

<script setup lang="ts">
import { Remark } from '@/services/remark/types';
import { Booking } from '@/services/booking/types';
import { User } from '@/services/user/types';
import { formatDate } from '@/utils/date';
import { defaultLanguage } from '@/i18n/tolgee';
import Image from 'primevue/image'
import moment from 'moment'
import { computed } from 'vue';
import { useTolgee } from '@tolgee/vue';

const props = defineProps<
    {
        remark: Remark;
        variant: 'secondary' | 'tertiary';
        // Optional booking context to show above the remark
        booking?: Booking;
    }>();

const fullName = (person?: User | null) => [person?.name, person?.last_name].filter(Boolean).join(' ');

const initials = computed(() => {
    const author = props.remark.author;
    return (author?.name?.[0] ?? '') + (author?.last_name?.[0] ?? '') || '?';
});

const authorName = computed(() => fullName(props.remark.author) || '—');

// getLanguage() is undefined until Tolgee has initialised, and moment().locale(undefined) is a
// getter returning a string, so .format() on it would throw and take the whole list down.
const tolgee = useTolgee(['language']);
const language = computed(() => tolgee.value.getLanguage() ?? defaultLanguage);

const createdAt = computed(() =>
    moment(props.remark.created_at).locale(language.value).format('DD/MM/YY - HH:mm'));

const bookingLine = computed(() => {
    const booking = props.booking;
    if (!booking) return '';
    const period = `${formatDate(booking.start_date, language.value, 'digit')} → ${formatDate(booking.end_date, language.value, 'digit')}`;
    return [fullName(booking.user), period].filter(Boolean).join(' · ');
});
</script>
